// src/components/zones/AirportZonesView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapPin, Navigation, Plus, Edit2, Trash2, Eye, X,
  Search, Filter, RefreshCw, Loader, Download, FileText, FileSpreadsheet,
  Plane, Fuel, Wrench, AlertTriangle, CheckCircle, XCircle, Clock
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertTriangle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertTriangle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Helper pour formater les erreurs ─────────────────────────────────────────
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  console.error('Full error:', error);
  
  if (error.response?.data) {
    const data = error.response.data;
    
    // Format standard de validation (array d'erreurs)
    if (Array.isArray(data)) {
      return data.map(e => {
        if (typeof e === 'string') return e;
        if (e.msg) return e.msg;
        if (e.message) return e.message;
        if (e.detail) return e.detail;
        return JSON.stringify(e);
      }).join('\n• ');
    }
    
    // Objet d'erreurs par champ
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`• ${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`• ${key}: ${value}`);
        } else if (value?.msg) {
          errors.push(`• ${key}: ${value.msg}`);
        }
      });
      if (errors.length > 0) return errors.join('\n');
    }
    
    // Messages directs
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
  }
  
  if (error.message) return error.message;
  return defaultMessage;
};

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Zone Type Badge ─────────────────────────────────────────────────────────
const ZoneTypeBadge = ({ type }) => {
  const config = {
    runway: { icon: Plane, color: 'text-blue-400 bg-blue-500/20', label: 'Runway' },
    taxiway: { icon: Navigation, color: 'text-cyan-400 bg-cyan-500/20', label: 'Taxiway' },
    apron: { icon: MapPin, color: 'text-purple-400 bg-purple-500/20', label: 'Apron' },
    movement_area: { icon: Navigation, color: 'text-indigo-400 bg-indigo-500/20', label: 'Movement Area' },
    fuel_farm: { icon: Fuel, color: 'text-orange-400 bg-orange-500/20', label: 'Fuel Farm' },
    terminal: { icon: MapPin, color: 'text-emerald-400 bg-emerald-500/20', label: 'Terminal' },
    maintenance: { icon: Wrench, color: 'text-yellow-400 bg-yellow-500/20', label: 'Maintenance' },
    security_zone: { icon: AlertTriangle, color: 'text-red-400 bg-red-500/20', label: 'Security Zone' },
    sida: { icon: AlertTriangle, color: 'text-red-400 bg-red-500/20', label: 'SIDA' },
    hangar_area: { icon: MapPin, color: 'text-teal-400 bg-teal-500/20', label: 'Hangar Area' },
    ramp: { icon: MapPin, color: 'text-slate-400 bg-slate-500/20', label: 'Ramp' }
  };
  const c = config[type] || { icon: MapPin, color: 'text-gray-400 bg-gray-500/20', label: type };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${c.color}`}>
      <Icon size={12} /> {c.label}
    </span>
  );
};

// ─── Runway Status Badge ─────────────────────────────────────────────────────
const RunwayStatusBadge = ({ status }) => {
  const config = {
    open: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20', label: 'OPEN' },
    closed: { icon: XCircle, color: 'text-red-400 bg-red-500/20', label: 'CLOSED' },
    maintenance: { icon: Wrench, color: 'text-yellow-400 bg-yellow-500/20', label: 'MAINTENANCE' },
    construction: { icon: AlertTriangle, color: 'text-orange-400 bg-orange-500/20', label: 'CONSTRUCTION' }
  };
  const c = config[status] || config.open;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
      <Icon size={10} /> {c.label}
    </span>
  );
};

// ─── Delete Modal ────────────────────────────────────────────────────────────
const DeleteModal = ({ zone, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [isHardDelete, setIsHardDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Delete Zone: {zone?.zone_code}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            Are you sure you want to delete <span className="text-white font-medium">{zone?.zone_code}</span>?
          </p>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Reason for deletion *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-red-500"
              placeholder="Explain why this zone is being deleted..."
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hardDelete"
              checked={isHardDelete}
              onChange={(e) => setIsHardDelete(e.target.checked)}
              className="w-4 h-4 accent-red-500"
            />
            <label htmlFor="hardDelete" className="text-red-400 text-sm">⚠️ Permanent hard delete (cannot be recovered)</label>
          </div>
          {isHardDelete && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-xs mb-2">Type <strong>CONFIRM_DELETE</strong> to confirm:</p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full bg-gray-900 border border-red-600 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="CONFIRM_DELETE"
              />
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason, isHardDelete, isHardDelete ? confirmText : null)}
            disabled={!reason || (isHardDelete && confirmText !== 'CONFIRM_DELETE')}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white text-sm"
          >
            Delete Zone
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Zone Form Modal ─────────────────────────────────────────────────────────
const ZoneFormModal = ({ zone, onSave, onClose, showToast }) => {
  const [formData, setFormData] = useState({
    zone_code: zone?.zone_code || '',
    zone_name: zone?.zone_name || '',
    zone_type: zone?.zone_type || 'runway',
    runway_designator: zone?.runway_designator || '',
    runway_length_ft: zone?.runway_length_ft || '',
    runway_width_ft: zone?.runway_width_ft || '',
    runway_surface: zone?.runway_surface || 'asphalt',
    runway_lighting: zone?.runway_lighting || false,
    runway_status: zone?.runway_status || 'open',
    description: zone?.description || '',
    latitude: zone?.latitude || '',
    longitude: zone?.longitude || '',
    is_active: zone?.is_active !== undefined ? zone.is_active : true
  });
  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.zone_code?.trim()) {
      newErrors.zone_code = 'Zone Code is required';
    }
    if (!formData.zone_name?.trim()) {
      newErrors.zone_name = 'Zone Name is required';
    }
    // Validate coordinates if provided
    if (formData.latitude && (formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      const errorMessages = Object.values(errors).filter(Boolean).join('\n• ');
      showToast(`Please fix the following errors:\n• ${errorMessages}`, 'error');
      return;
    }
    
    // Prepare payload with proper types
    const payload = {
      ...formData,
      zone_code: formData.zone_code.trim().toUpperCase(),
      zone_name: formData.zone_name.trim(),
      runway_length_ft: formData.runway_length_ft ? parseFloat(formData.runway_length_ft) : null,
      runway_width_ft: formData.runway_width_ft ? parseFloat(formData.runway_width_ft) : null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    };
    
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
          <h3 className="text-white font-semibold">
            {zone ? `Edit Zone: ${zone.zone_code}` : 'Create New Zone'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Zone Code *</label>
              <input
                type="text"
                value={formData.zone_code}
                onChange={(e) => updateField('zone_code', e.target.value.toUpperCase())}
                className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${
                  errors.zone_code ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="e.g., RWY-18L"
              />
              {errors.zone_code && <p className="text-red-400 text-xs mt-1">{errors.zone_code}</p>}
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Zone Name *</label>
              <input
                type="text"
                value={formData.zone_name}
                onChange={(e) => updateField('zone_name', e.target.value)}
                className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${
                  errors.zone_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="e.g., Runway 18L"
              />
              {errors.zone_name && <p className="text-red-400 text-xs mt-1">{errors.zone_name}</p>}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Zone Type</label>
              <select
                value={formData.zone_type}
                onChange={(e) => updateField('zone_type', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="runway">Runway</option>
                <option value="taxiway">Taxiway</option>
                <option value="apron">Apron</option>
                <option value="movement_area">Movement Area</option>
                <option value="fuel_farm">Fuel Farm</option>
                <option value="terminal">Terminal</option>
                <option value="maintenance">Maintenance</option>
                <option value="security_zone">Security Zone</option>
                <option value="sida">SIDA</option>
                <option value="hangar_area">Hangar Area</option>
                <option value="ramp">Ramp</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Status</label>
              <select
                value={formData.runway_status}
                onChange={(e) => updateField('runway_status', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="maintenance">Maintenance</option>
                <option value="construction">Construction</option>
              </select>
            </div>
          </div>
          
          {/* Runway specific fields */}
          {formData.zone_type === 'runway' && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Runway Designator</label>
                  <input
                    type="text"
                    value={formData.runway_designator}
                    onChange={(e) => updateField('runway_designator', e.target.value.toUpperCase())}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="18L/36R"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Length (ft)</label>
                  <input
                    type="number"
                    step="1"
                    value={formData.runway_length_ft}
                    onChange={(e) => updateField('runway_length_ft', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="5000"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Width (ft)</label>
                  <input
                    type="number"
                    step="1"
                    value={formData.runway_width_ft}
                    onChange={(e) => updateField('runway_width_ft', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Surface</label>
                  <select
                    value={formData.runway_surface}
                    onChange={(e) => updateField('runway_surface', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="asphalt">Asphalt</option>
                    <option value="concrete">Concrete</option>
                    <option value="grooved">Grooved</option>
                    <option value="composite">Composite</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="runway_lighting"
                    checked={formData.runway_lighting}
                    onChange={(e) => updateField('runway_lighting', e.target.checked)}
                    className="w-4 h-4 accent-blue-500"
                  />
                  <label htmlFor="runway_lighting" className="text-gray-300 text-sm">Lighting Installed</label>
                </div>
              </div>
            </>
          )}
          
          {/* Coordinates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => updateField('latitude', e.target.value)}
                className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white text-sm ${
                  errors.latitude ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="29.7947"
              />
              {errors.latitude && <p className="text-red-400 text-xs mt-1">{errors.latitude}</p>}
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => updateField('longitude', e.target.value)}
                className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white text-sm ${
                  errors.longitude ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="-98.7318"
              />
              {errors.longitude && <p className="text-red-400 text-xs mt-1">{errors.longitude}</p>}
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-xs block mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Additional information about this zone..."
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <label htmlFor="is_active" className="text-gray-300 text-sm">Active</label>
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm">
            {zone ? 'Update Zone' : 'Create Zone'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AirportZonesView = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [deletingZone, setDeletingZone] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalRunways: 0,
    totalTaxiways: 0,
    openRunways: 0,
    activeZones: 0
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) {
          setLogoUrl(`${API_BASE}${response.data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  // Fetch zones
  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/airport/zones/`);
      const data = response.data || [];
      setZones(data);
      
      // Calculate stats
      const runways = data.filter(z => z.zone_type === 'runway');
      const taxiways = data.filter(z => z.zone_type === 'taxiway');
      const openRunways = runways.filter(r => r.runway_status === 'open').length;
      
      setStats({
        totalRunways: runways.length,
        totalTaxiways: taxiways.length,
        openRunways,
        activeZones: data.filter(z => z.is_active).length
      });
    } catch (error) {
      console.error('Error fetching zones:', error);
      showToast(formatErrorMessage(error, 'Error fetching zones'), 'error');
      setZones([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => {
    fetchCompanySettings();
    fetchZones();
  }, [fetchZones, fetchCompanySettings]);

  // Filter zones
  const filteredZones = zones.filter(zone => {
    const matchesSearch = 
      zone.zone_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      zone.zone_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || zone.zone_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || zone.runway_status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Create/Update zone
  const handleSaveZone = async (formData) => {
    setSaving(true);
    try {
      if (editingZone) {
        await axios.put(`${API_BASE}/airport/zones/${editingZone.id}`, formData);
        showToast(`Zone "${formData.zone_code}" updated successfully`, 'success');
      } else {
        await axios.post(`${API_BASE}/airport/zones/`, formData);
        showToast(`Zone "${formData.zone_code}" created successfully`, 'success');
      }
      await fetchZones();
      setShowForm(false);
      setEditingZone(null);
    } catch (error) {
      console.error('Error saving zone:', error);
      const errorMessage = formatErrorMessage(error, 'Failed to save zone');
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete zone
  const handleDeleteZone = async (reason, isHardDelete, confirmText) => {
    if (isHardDelete && confirmText !== 'CONFIRM_DELETE') {
      showToast('Please type CONFIRM_DELETE to confirm hard deletion', 'error');
      return;
    }
    
    try {
      const endpoint = isHardDelete 
        ? `${API_BASE}/airport/zones/${deletingZone.id}/hard`
        : `${API_BASE}/airport/zones/${deletingZone.id}`;
      
      await axios.delete(endpoint, { data: { reason } });
      showToast(`Zone "${deletingZone.zone_code}" deleted successfully`, 'success');
      await fetchZones();
      setDeletingZone(null);
    } catch (error) {
      console.error('Error deleting zone:', error);
      showToast(formatErrorMessage(error, 'Error deleting zone'), 'error');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const exportData = filteredZones.map(zone => ({
        'Zone Code': zone.zone_code,
        'Zone Name': zone.zone_name,
        'Type': zone.zone_type,
        'Runway Designator': zone.runway_designator || 'N/A',
        'Length (ft)': zone.runway_length_ft || 'N/A',
        'Width (ft)': zone.runway_width_ft || 'N/A',
        'Surface': zone.runway_surface || 'N/A',
        'Lighting': zone.runway_lighting ? 'Yes' : 'No',
        'Status': zone.runway_status,
        'Active': zone.is_active ? 'Yes' : 'No',
        'Latitude': zone.latitude || 'N/A',
        'Longitude': zone.longitude || 'N/A'
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Airport Zones');
      XLSX.writeFile(wb, `airport_zones_${new Date().toISOString().slice(0,19)}.xlsx`);
      showToast('Excel export completed', 'success');
    } catch (error) {
      showToast('Error exporting to Excel', 'error');
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    try {
      const doc = new jsPDF();
      const companyName = company?.name || 'AeroTrick FBO';
      
      if (logoUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.src = logoUrl;
          await new Promise((resolve) => {
            img.onload = () => {
              doc.addImage(img, 'PNG', 14, 10, 40, 15);
              resolve();
            };
            img.onerror = resolve;
          });
        } catch (e) {}
      }
      
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175);
      doc.text('Airport Zones Report', 70, 20);
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Runways: ${stats.totalRunways}`, 14, 45);
      doc.text(`Taxiways: ${stats.totalTaxiways}`, 80, 45);
      doc.text(`Open Runways: ${stats.openRunways}`, 150, 45);
      
      const tableData = filteredZones.map(zone => [
        zone.zone_code,
        zone.zone_name,
        zone.zone_type,
        zone.runway_status || 'N/A',
        zone.is_active ? 'Active' : 'Inactive'
      ]);
      
      doc.autoTable({
        startY: 55,
        head: [['Code', 'Name', 'Type', 'Status', 'Active']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 }
      });
      
      const finalY = doc.lastAutoTable?.finalY || 250;
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
        14,
        finalY + 20
      );
      
      doc.save(`airport_zones_${new Date().toISOString().slice(0,19)}.pdf`);
      showToast('PDF export completed', 'success');
    } catch (error) {
      showToast('Error exporting to PDF', 'error');
    }
  };

  return (
    <div className="space-y-4 px-1">
      
      {/* ── Header with Logo ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt="Company Logo" 
              className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <MapPin size={18} className="text-blue-400" />
              </div>
              Airport Zones Management
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — Runways, Taxiways, Aprons
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportToExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs">
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button onClick={exportToPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <FileText size={12} /> PDF
          </button>
          <button onClick={fetchZones}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Total Runways" value={stats.totalRunways} icon={Plane} color="#38bdf8" />
        <StatCard title="Taxiways" value={stats.totalTaxiways} icon={Navigation} color="#10b981" />
        <StatCard title="Open Runways" value={stats.openRunways} icon={CheckCircle} color="#22c55e" />
        <StatCard title="Active Zones" value={stats.activeZones} icon={MapPin} color="#a78bfa" />
      </div>
      
      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by zone code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="runway">Runways</option>
          <option value="taxiway">Taxiways</option>
          <option value="apron">Aprons</option>
          <option value="fuel_farm">Fuel Farms</option>
        </select>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="maintenance">Maintenance</option>
          <option value="construction">Construction</option>
        </select>
        
        <button
          onClick={() => { setEditingZone(null); setShowForm(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
        >
          <Plus size={14} /> Add Zone
        </button>
      </div>
      
      {/* ── Zones Table ── */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Code</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Name</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Type</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Runway Info</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Active</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <Loader size={24} className="animate-spin mx-auto mb-2" />
                    Loading zones...
                    </td>
                </tr>
              ) : filteredZones.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    No zones found
                    </td>
                </tr>
              ) : (
                filteredZones.map((zone) => (
                  <tr key={zone.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-white font-mono text-sm">{zone.zone_code}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{zone.zone_name}</td>
                    <td className="px-4 py-3"><ZoneTypeBadge type={zone.zone_type} /></td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {zone.zone_type === 'runway' ? (
                        <>
                          {zone.runway_designator && <div>{zone.runway_designator}</div>}
                          {zone.runway_length_ft && <div>{zone.runway_length_ft}' × {zone.runway_width_ft}'</div>}
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {zone.zone_type === 'runway' ? (
                        <RunwayStatusBadge status={zone.runway_status} />
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {zone.is_active ? (
                        <span className="text-green-400 text-xs">Active</span>
                      ) : (
                        <span className="text-gray-500 text-xs">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditingZone(zone); setShowForm(true); }}
                          className="p-1 hover:bg-gray-600 rounded text-yellow-400"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeletingZone(zone)}
                          className="p-1 hover:bg-gray-600 rounded text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
           </table>
        </div>
      </div>
      
      {/* ── Footer ── */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* ── Modals ── */}
      {showForm && (
        <ZoneFormModal
          zone={editingZone}
          onSave={handleSaveZone}
          onClose={() => { setShowForm(false); setEditingZone(null); }}
          showToast={showToast}
        />
      )}
      
      {deletingZone && (
        <DeleteModal
          zone={deletingZone}
          onConfirm={handleDeleteZone}
          onClose={() => setDeletingZone(null)}
        />
      )}
      
      {/* ── Toast Notification ── */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
    </div>
  );
};

export default AirportZonesView;